import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  BookOpen,
  Headphones,
  TrendingUp,
  Sun,
  Moon,
  Settings as SettingsIcon,
  User as UserIcon,
  LogOut,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../../ui/utils';
import { authService, AuthState } from '../../data/sync/authService';
import { mockLearnerProfile } from '../../mocks/learnerProfile';

export const AppHeader: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>(authService.getState());
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const unsubAuth = authService.subscribe((s) => setAuthState(s));

    const handleClickOutside = (event: MouseEvent) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target as Node)
      ) {
        setIsAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      unsubAuth();
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = [
    { to: '/dashboard', label: 'Home', icon: Sparkles },
    { to: '/training', label: 'Learn', icon: BookOpen },
    { to: '/simulator/call/SCEN-SIM-01', label: 'Practice', icon: Headphones },
    { to: '/performance', label: 'Progress', icon: TrendingUp },
  ];

  const displayName =
    authState.user?.displayName ||
    (authState.user?.email ? authState.user.email.split('@')[0] : mockLearnerProfile.name);
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleSignOut = async () => {
    setIsAccountMenuOpen(false);
    await authService.signOut();
  };

  return (
    <header className="sticky top-0 z-30 bg-surface-1/90 backdrop-blur-md border-b border-border-subtle/80 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        {/* Brand Anchor */}
        <div className="flex items-center gap-6">
          <NavLink
            to="/dashboard"
            className="flex items-center gap-2.5 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus rounded-lg py-1 px-1.5"
            aria-label="CallFoundry Home"
          >
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand to-brand-hover text-white flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="w-4 h-4">
                <path d="M12 3v18M18 7l-6-4-6 4M18 17l-6 4-6-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base font-bold tracking-tight text-foreground-primary">
                CallFoundry
              </span>
            </div>
          </NavLink>

          {/* Desktop Primary Navigation */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Primary Navigation">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive =
                item.to === '/dashboard'
                  ? location.pathname === '/dashboard' || location.pathname === '/'
                  : location.pathname.startsWith(item.to.split('/')[1] ? `/${item.to.split('/')[1]}` : item.to);

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-sm font-medium transition-all duration-150',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
                    isActive
                      ? 'bg-brand-soft text-brand font-semibold shadow-xs'
                      : 'text-foreground-secondary hover:text-foreground-primary hover:bg-surface-2'
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* Right Side Controls */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Theme Toggle Button */}
          <button
            type="button"
            onClick={toggleTheme}
            className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-xl bg-surface-2 hover:bg-surface-3 active:scale-95 border border-border-subtle text-foreground-secondary hover:text-foreground-primary flex items-center justify-center transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? (
              <Sun className="w-4 h-4 text-status-warning" />
            ) : (
              <Moon className="w-4 h-4 text-foreground-secondary" />
            )}
          </button>

          {/* User Account Menu */}
          <div className="relative" ref={accountMenuRef}>
            <button
              type="button"
              onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
              className={cn(
                'flex items-center gap-2 pl-2 pr-3 min-h-[44px] rounded-xl border transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
                isAccountMenuOpen || location.pathname === '/settings'
                  ? 'bg-brand-soft border-brand/30 text-brand'
                  : 'bg-surface-2 hover:bg-surface-3 border-border-subtle text-foreground-primary'
              )}
              aria-label="User account and settings menu"
              aria-expanded={isAccountMenuOpen}
            >
              <div className="w-7 h-7 rounded-lg bg-brand/15 text-brand flex items-center justify-center text-xs font-bold">
                {initials || <UserIcon className="w-3.5 h-3.5" />}
              </div>
              <span className="text-xs font-medium hidden lg:inline truncate max-w-[100px]">
                {displayName.split(' ')[0]}
              </span>
              <SettingsIcon className="w-3.5 h-3.5 text-foreground-muted" />
            </button>

            {/* Account Popover Menu */}
            {isAccountMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-surface-1 rounded-2xl shadow-xl border border-border-subtle p-3 space-y-2 z-50 animate-in fade-in zoom-in-95 duration-150">
                <div className="px-2 py-1.5 border-b border-border-subtle/60">
                  <span className="text-xs font-bold text-foreground-primary block truncate">
                    {displayName}
                  </span>
                  <span className="text-[11px] text-foreground-secondary block truncate">
                    {authState.user?.email || ''}
                  </span>
                </div>

                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={() => {
                      setIsAccountMenuOpen(false);
                      navigate('/settings');
                    }}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-xs font-medium text-foreground-primary hover:bg-surface-2 transition-colors text-left"
                  >
                    <SettingsIcon className="w-4 h-4 text-foreground-muted" />
                    <span>Settings</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-xs font-medium text-status-critical hover:bg-status-critical/10 transition-colors text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
